// use-last-seen — holt den letzten Besuch EINMAL pro Page-Load (nicht pro Mount).
//
// Warum once-per-session: jeder Server-Aufruf rotiert current→prev. Würde der
// Hook bei jedem Remount (Tab-Wechsel) neu pingen, wäre `prev_at` schnell „vor
// 2 Minuten" statt die echte letzte Sitzung. Ein Modul-Singleton cached den
// ersten Ping für die Dauer des Page-Loads; ein harter Reload pingt neu.

import { useEffect, useState } from 'react';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { pingLastSeen, type LastSeenResponse } from '@/lib/last-seen-api';

let cached: LastSeenResponse | null = null;
let inflight: Promise<LastSeenResponse> | null = null;

async function loadOnce(accessToken: string): Promise<LastSeenResponse> {
  if (cached) return cached;
  if (!inflight) {
    inflight = pingLastSeen(accessToken)
      .then((res) => {
        cached = res;
        return res;
      })
      .catch((err) => {
        inflight = null; // erneuter Versuch beim nächsten Mount erlaubt
        throw err;
      });
  }
  return inflight;
}

/** Nur für Tests: Modul-Cache zurücksetzen. */
export function __resetLastSeenCache(): void {
  cached = null;
  inflight = null;
}

export function useLastSeen(): LastSeenResponse | null {
  const accessToken = useAuthStore(selectAccessToken);
  const [lastSeen, setLastSeen] = useState<LastSeenResponse | null>(cached);

  useEffect(() => {
    if (!accessToken || lastSeen) return;
    let active = true;
    loadOnce(accessToken)
      .then((res) => {
        if (active) setLastSeen(res);
      })
      .catch(() => {
        /* still — Hero fällt auf Erstbesuch-Begrüßung zurück */
      });
    return () => {
      active = false;
    };
  }, [accessToken, lastSeen]);

  return lastSeen;
}
